import React, { useState, useEffect } from "react";
import {
  LocalizationProvider,
  DateRangePicker as DateRangePickerBase,
} from "@mui/x-date-pickers-pro";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { Box, Typography } from "@mui/material";
import EventIcon from "@mui/icons-material/Event";

import moment, { type Moment } from "moment";
import { logger } from "../helpers/loggerHelper";
import { toastr } from "react-redux-toastr";
import {
  createDatePickerInputClickHandler,
  removeLicenseKeyWatermark,
} from "../helpers/utils";

type DateRangePart = {
  year: number | string;
  month: number | string;
  date: number | string;
};

type InitialDateRange = {
  from: DateRangePart;
  to: DateRangePart;
};

type DateRangeValue = [Moment | null, Moment | null] | null;

type MUIDateRangeProps = {
  onChangeDate: (_value: [string | null, string | null]) => void;
  initialRange?: InitialDateRange | null;
  disableRangeSelection?: boolean;
  label?: React.ReactNode;
  labelProps?: Record<string, unknown>;
  isEndDateRequired?: boolean;
  format?: string;
  disablePast?: boolean;
  isOptional?: boolean;
  size?: "small" | "medium";
  disabled?: boolean;
  [key: string]: unknown;
};

const DateRangePicker = DateRangePickerBase as unknown as React.ComponentType<
  Record<string, unknown>
>;

const MUIDateRange = ({
  onChangeDate,
  initialRange,
  disableRangeSelection,
  label,
  labelProps,
  isEndDateRequired = false,
  format = "DD MMM, YYYY",
  disablePast = false,
  isOptional = false,
  size = "small",
  disabled = false,
  ...props
}: MUIDateRangeProps) => {
  const today = moment().startOf("day");
  const validInitialRange =
    initialRange && initialRange.from && initialRange.to;

  const initialStart = isOptional
    ? null
    : validInitialRange
      ? moment(
          `${initialRange.from.year}-${initialRange.from.month}-${initialRange.from.date}`,
          "YYYY-MM-DD"
        )
      : today;

  const initialEnd = isOptional
    ? null
    : validInitialRange
      ? moment(
          `${initialRange.to.year}-${initialRange.to.month}-${initialRange.to.date}`,
          "YYYY-MM-DD"
        )
      : today;
  const [value, setValue] = useState<DateRangeValue>([
    initialStart,
    initialEnd,
  ]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isOptional && !validInitialRange) {
      setValue([null, null]);
    } else if (validInitialRange) {
      setValue([
        moment(
          `${initialRange.from.year}-${initialRange.from.month}-${initialRange.from.date}`,
          "YYYY-MM-DD"
        ),
        moment(
          `${initialRange.to.year}-${initialRange.to.month}-${initialRange.to.date}`,
          "YYYY-MM-DD"
        ),
      ]);
    }
  }, [initialRange, validInitialRange, isOptional]);

  const handleChange = (newValue: DateRangeValue) => {
    setValue(newValue);

    const [start, end] = newValue || [];

    // Case: Both dates selected
    if (start && end) {
      if (start.isValid() && end.isValid()) {
        if (isEndDateRequired) {
          onChangeDate(
            newValue.map((d) => d?.format("YYYY-MM-DD")) as [string, string]
          );
        } else {
          onChangeDate(
            newValue.map((date) => date?.format("YYYY-MM-DD")) as [
              string,
              string,
            ]
          );
          setValue([newValue[0], newValue[1]]);
        }
      } else {
        logger.error("Invalid date values in range", { newValue });
        onChangeDate([null, null]);
        setValue([null, null]);
      }
      return;
    }

    // Case: Only start date selected
    if (start && !end) {
      if (!start.isValid()) {
        logger.error("Invalid start date", { start });
        return;
      }
      if (isEndDateRequired) {
        setValue([newValue[0], null]);
        toastr.error("Error", "End date is required", {
          id: "end_date_required",
        });
      } else {
        onChangeDate([
          newValue[0].format("YYYY-MM-DD"),
          newValue[0].format("YYYY-MM-DD"),
        ]);
        setValue([newValue[0], newValue[0]]);
      }
      return;
    }

    // Case: No dates selected
    if (!start && !end) {
      if (isOptional) {
        onChangeDate([null, null]);
        setValue([null, null]);
      } else {
        // For required fields, if no valid dates, set to today
        onChangeDate([today.format("YYYY-MM-DD"), today.format("YYYY-MM-DD")]);
        setValue([today, today]);
      }
    }
  };

  const shouldDisableDate = (date: Moment) => {
    if (disablePast && moment(date).isBefore(today, "day")) {
      return true;
    }

    // Set reasonable date boundaries (5 years in each direction)
    const minDate = moment().subtract(5, "years").startOf("day");
    const maxDate = moment().add(5, "years").endOf("day");

    return (
      moment(date).isBefore(minDate, "day") ||
      moment(date).isAfter(maxDate, "day")
    );
  };
  const handleInputClick = () => {
    createDatePickerInputClickHandler(
      () => setIsOpen(true),
      disabled || disableRangeSelection,
      isOpen
    )();

    setTimeout(removeLicenseKeyWatermark, 100);
  };

  return (
    <Box sx={{ width: "100%" }}>
      {label && (
        <Typography variant="caption" className="fw-sb" {...labelProps}>
          {label}
        </Typography>
      )}
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <DateRangePicker
          value={value}
          onChange={handleChange}
          disabled={disableRangeSelection}
          format={format}
          disablePast={disablePast}
          shouldDisableDate={shouldDisableDate}
          size={size}
          open={isOpen}
          onOpen={() => {
            setIsOpen(true);
            // Use setTimeout to ensure the calendar is fully rendered before removing watermark
            setTimeout(() => {
              removeLicenseKeyWatermark();
            }, 50);
          }}
          onClose={() => setIsOpen(false)}
          slots={{
            openPickerIcon: EventIcon,
          }}
          slotProps={{
            textField: {
              variant: "outlined",
              size: size,
              fullWidth: true,
              borderRadius: "var(--radius-control)",
              disabled: disabled,
              InputLabelProps: { shrink: false },
              readOnly: true,
              onClick: handleInputClick,
              inputProps: {
                style: {
                  color: disabled ? "var(--neutral-500)" : "var(--neutral-700)",
                  fontSize: "12px",
                },
              },
              InputProps: {
                style: {
                  color: disabled ? "var(--neutral-500)" : "var(--neutral-700)",
                  fontSize: "12px",
                },
              },
              sx: (theme: any) => ({
                color: disabled
                  ? theme.palette.action.disabled
                  : theme.palette.text.primary,
                backgroundColor: disabled
                  ? theme.palette.action.disabledBackground
                  : theme.palette.background.paper,
                borderRadius: "var(--radius-control)",
                border: "none",
                userSelect: "none",
              }),
            },
            openPickerButton: {
              disabled: disabled,
              sx: (theme: any) => ({
                color: disabled
                  ? theme.palette.action.disabled
                  : theme.palette.primary.main,
              }),
            },
          }}
          {...props}
        />
      </LocalizationProvider>
    </Box>
  );
};

MUIDateRange.displayName = "MUIDateRange";

export default MUIDateRange;
